import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, MapPin } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CityDirectory = () => {
  const navigate = useNavigate();
  const [cities, setCities] = useState([]);
  const [listings, setListings] = useState([]);
  const [activeCity, setActiveCity] = useState(null);
  const [hoverCity, setHoverCity] = useState(null);
  const [activeCat, setActiveCat] = useState("All");

  useEffect(() => {
    axios.get(`${API}/cities`).then((r) => {
      setCities(r.data);
      if (r.data.length) setActiveCity(r.data[0].name);
    });
    axios.get(`${API}/listings`).then((r) => setListings(r.data));
  }, []);

  const bgImage = useMemo(() => {
    const c = cities.find((x) => x.name === (hoverCity || activeCity));
    return c?.image;
  }, [cities, hoverCity, activeCity]);

  const cityListings = listings.filter((l) => l.city === activeCity);
  const cats = ["All", ...Array.from(new Set(cityListings.map((l) => l.category)))];
  const visible =
    activeCat === "All"
      ? cityListings
      : cityListings.filter((l) => l.category === activeCat);

  return (
    <section
      id="directory"
      data-testid="directory-section"
      className="relative bg-[#0a0a0a] text-white overflow-hidden"
    >
      {/* shifting backdrop */}
      <AnimatePresence mode="wait">
        {bgImage && (
          <motion.div
            key={bgImage}
            initial={{ opacity: 0, scale: 1.05 }}
            animate={{ opacity: 0.28, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.9, ease: "easeOut" }}
            className="absolute inset-0"
          >
            <img src={bgImage} alt="" className="h-full w-full object-cover" />
          </motion.div>
        )}
      </AnimatePresence>
      <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/60 to-black/90" />

      <div className="relative px-4 md:px-8 lg:px-12 py-24 md:py-32">
        <div className="max-w-2xl">
          <span className="font-accent uppercase text-xs tracking-[0.3em] text-white/50">
            The Directory
          </span>
          <h2 className="font-serif-display text-5xl md:text-7xl tracking-tight leading-[0.9] mt-3">
            Curated by
            <br />
            <span className="italic font-light">city &amp; craft</span>
          </h2>
          <p className="mt-6 font-light text-white/70 leading-relaxed">
            The businesses worth knowing — organized by every major Texas city
            and the things you actually care about. Featured partners rise to the
            top.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* City list */}
          <div className="lg:col-span-5">
            <ul data-testid="city-list">
              {cities.map((c) => {
                const isActive = c.name === activeCity;
                return (
                  <li
                    key={c.name}
                    className="border-b border-white/15"
                    onMouseEnter={() => setHoverCity(c.name)}
                    onMouseLeave={() => setHoverCity(null)}
                  >
                    <button
                      data-testid={`city-btn-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
                      onClick={() => {
                        setActiveCity(c.name);
                        setActiveCat("All");
                      }}
                      className="w-full text-left py-5 flex items-baseline justify-between group"
                    >
                      <span
                        className={`font-serif-display uppercase tracking-tight leading-none transition-all duration-300 text-4xl md:text-6xl ${
                          isActive
                            ? "text-white italic"
                            : "text-white/40 group-hover:text-white"
                        }`}
                      >
                        {c.name}
                      </span>
                      <span className="font-accent text-xs tracking-[0.2em] text-white/50 shrink-0 ml-4">
                        {String(c.count).padStart(2, "0")}
                      </span>
                    </button>
                    {isActive && (
                      <p className="pb-5 -mt-1 font-light text-white/60 text-sm flex items-center gap-2">
                        <MapPin size={13} /> {c.tagline}
                      </p>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Listings */}
          <div className="lg:col-span-7">
            <div className="flex flex-wrap gap-2 mb-7" data-testid="category-filters">
              {cats.map((cat) => (
                <button
                  key={cat}
                  data-testid={`cat-filter-${cat.toLowerCase().replace(/[^a-z]/g, "")}`}
                  onClick={() => setActiveCat(cat)}
                  className={`font-accent uppercase text-[11px] tracking-[0.18em] px-4 py-2 border transition-colors duration-300 ${
                    activeCat === cat
                      ? "bg-white text-black border-white"
                      : "border-white/30 text-white/70 hover:border-white"
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>

            <div className="grid sm:grid-cols-2 gap-5" data-testid="listings-grid">
              <AnimatePresence mode="popLayout">
                {visible.map((l, i) => (
                  <motion.div
                    key={l.id}
                    layout
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.4, delay: i * 0.04 }}
                    onClick={() => navigate(`/listing/${l.id}`)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === "Enter" && navigate(`/listing/${l.id}`)}
                    data-testid={`listing-card-${l.id}`}
                    className="group block cursor-pointer bg-white/5 border border-white/10 hover:border-white/40 transition-colors duration-300"
                  >
                    <div className="relative aspect-[16/10] overflow-hidden">
                      <img
                        src={l.image}
                        alt={l.name}
                        className="h-full w-full object-cover transition-transform duration-[1.1s] ease-out group-hover:scale-105"
                      />
                      {l.featured && (
                        <span className="absolute top-3 left-3 bg-white text-black font-accent uppercase text-[9px] tracking-[0.2em] px-2.5 py-1">
                          Featured
                        </span>
                      )}
                    </div>
                    <div className="p-5">
                      <span className="font-accent uppercase text-[10px] tracking-[0.22em] text-white/50">
                        {l.category}
                      </span>
                      <h3 className="font-serif-display text-2xl leading-tight mt-1 flex items-start justify-between gap-2">
                        {l.name}
                        <ArrowUpRight
                          size={18}
                          className="mt-1 shrink-0 text-white/40 group-hover:text-white transition-colors"
                        />
                      </h3>
                      <p className="mt-2 font-light text-sm text-white/65 leading-relaxed">
                        {l.blurb}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CityDirectory;
